import './settings-leaf.css';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { companyApi } from '../../api/endpoints.ts';
import { ApiRequestError } from '../../api/client.ts';
import { useSession } from '../../state/session.tsx';
import { HelpDocLink, NUMBER_CUTOVER_DOC_SLUG } from '../help-doc-link.tsx';
import { Alert, Button, Checkbox, LoadingBlock, Select, TextArea, TextInput } from '../ui.tsx';
import { PlanRequiredNotice, usePlanFeature } from '../plan-gate.tsx';
import {
  RING_TIMEOUT_DEFAULT_SECONDS,
  RING_TIMEOUT_MAX_SECONDS,
  RING_TIMEOUT_MIN_SECONDS,
  VOICE_GREETING_MAX_CHARS,
  AFTER_HOURS_OPTIONS,
  VOICEMAIL_NOTIFY_OPTIONS,
  type AfterHoursPolicy,
  type GreetingKind,
  type VoicemailNotify,
  type VoiceSettings,
} from '@shared/voice.ts';

const RING_OPTIONS = Array.from(
  { length: (RING_TIMEOUT_MAX_SECONDS - RING_TIMEOUT_MIN_SECONDS) / 5 + 1 },
  (_, index) => RING_TIMEOUT_MIN_SECONDS + index * 5,
);

interface Draft {
  forwardTo: string;
  ringTimeoutSeconds: number;
  greetingKind: GreetingKind;
  greetingText: string;
  recordAnswered: boolean;
  transcribe: boolean;
  voicemailNotify: VoicemailNotify;
  afterHours: AfterHoursPolicy;
}

function draftFrom(settings: VoiceSettings): Draft {
  return {
    forwardTo: settings.forwardTo ?? '',
    ringTimeoutSeconds: settings.ringTimeoutSeconds,
    greetingKind: settings.greetingKind,
    greetingText: settings.greetingText ?? '',
    recordAnswered: settings.recordAnswered,
    transcribe: settings.transcribe,
    voicemailNotify: settings.voicemailNotify,
    afterHours: settings.afterHours,
  };
}

/**
 * Settings › Text messaging › Calls: what happens when a client calls the
 * company's BlastoClean number. It rings the phone saved here; an unanswered
 * call plays the greeting and takes a voicemail, which arrives in Messages
 * with its recording and transcript.
 *
 * Shown only where the installation answers calls. Below Pro the card is the
 * upgrade notice; the Worker refuses the settings regardless.
 */
export default function VoiceSettingsCard() {
  const { t } = useTranslation();
  const { company, can } = useSession();
  const entitled = usePlanFeature('voice_inbox');
  if (!company?.voiceInboxAvailable || !can('company:write')) return null;
  return (
    <section className="card work-section settings-leaf mb-6" aria-labelledby="voice-settings-heading">
      <div className="work-section-heading">
        <h2 id="voice-settings-heading" className="mb-2 text-lg font-semibold">
          {t('voiceSettings.heading')}
        </h2>
      </div>
      <p className="mb-4 text-sm text-[color:var(--color-muted)]">
        {t('voiceSettings.description')}
        <HelpDocLink slug={NUMBER_CUTOVER_DOC_SLUG}>{t('smsSettings.cutoverGuide')}</HelpDocLink>
      </p>
      {entitled ? (
        <VoiceSettingsForm />
      ) : (
        <>
          <RecordingsLeftNotice />
          <PlanRequiredNotice feature="voice_inbox" />
        </>
      )}
    </section>
  );
}

/**
 * Without the plan the settings (and their count) are not served, but
 * recordings left on a Twilio account the company let go still need deleting
 * there: asked for on their own, from the one call-settings read the plan
 * does not gate.
 */
function RecordingsLeftNotice() {
  const { t } = useTranslation();
  const query = useQuery({ queryKey: ['voice-recordings-left'], queryFn: () => companyApi.voiceRecordingsLeft() });
  const recordingsLeft = query.data?.recordingsLeftOnTwilio ?? 0;
  if (recordingsLeft <= 0) return null;
  return (
    <div className="mb-3">
      <Alert tone="warning">{t('voiceSettings.recordingsLeftOnTwilio', { count: recordingsLeft })}</Alert>
    </div>
  );
}

function VoiceSettingsForm() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['voice-settings'], queryFn: () => companyApi.voiceSettings() });
  const [draft, setDraft] = useState<Draft | null>(null);
  const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const settings = query.data?.settings;

  useEffect(() => {
    if (settings && draft === null) setDraft(draftFrom(settings));
  }, [settings, draft]);

  const applied = (data: { settings: VoiceSettings }, text: string) => {
    queryClient.setQueryData(['voice-settings'], data);
    setDraft(draftFrom(data.settings));
    setFieldErrors({});
    setMessage({ tone: 'success', text });
  };
  // A greeting upload or removal changes only the greeting: the rest of the
  // form may hold edits the owner has not saved yet, and they stay. Only a
  // spoken greeting remains to choose once the recording is gone.
  const greetingApplied = (data: { settings: VoiceSettings }, text: string) => {
    queryClient.setQueryData(['voice-settings'], data);
    setDraft((current) =>
      current && current.greetingKind === 'audio' && !data.settings.hasGreetingAudio
        ? { ...current, greetingKind: 'tts' }
        : current,
    );
    setFieldErrors(({ greetingKind: _greetingKind, ...rest }) => rest);
    setMessage({ tone: 'success', text });
  };
  const failed = (caught: unknown, fallback: string) => {
    if (caught instanceof ApiRequestError) {
      setFieldErrors(caught.fields);
      setMessage({ tone: 'error', text: caught.message });
    } else setMessage({ tone: 'error', text: fallback });
  };

  const save = useMutation({
    mutationFn: (value: Draft) =>
      companyApi.saveVoiceSettings({
        forwardTo: value.forwardTo.trim() || null,
        ringTimeoutSeconds: value.ringTimeoutSeconds,
        greetingKind: value.greetingKind,
        greetingText: value.greetingText.trim() || null,
        recordAnswered: value.recordAnswered,
        transcribe: value.transcribe,
        voicemailNotify: value.voicemailNotify,
        afterHours: value.afterHours,
      }),
    onSuccess: (data) => applied(data, t('voiceSettings.saved')),
    onError: (caught) => failed(caught, t('voiceSettings.saveError')),
  });
  const greetingInput = useRef<HTMLInputElement>(null);
  const upload = useMutation({
    mutationFn: (file: File) => companyApi.uploadVoiceGreeting(file),
    onSuccess: (data) => greetingApplied(data, t('voiceSettings.greetingUploaded')),
    onError: (caught) => failed(caught, t('voiceSettings.saveError')),
  });
  const removeGreeting = useMutation({
    mutationFn: () => companyApi.deleteVoiceGreeting(),
    onSuccess: (data) => greetingApplied(data, t('voiceSettings.greetingRemoved')),
    onError: (caught) => failed(caught, t('voiceSettings.saveError')),
  });

  if (query.isLoading) return <LoadingBlock label={t('voiceSettings.loading')} />;
  if (query.isError || !settings || !draft)
    return (
      <Alert tone="error">
        {t('voiceSettings.loadError')}{' '}
        <Button variant="secondary" onClick={() => void query.refetch()}>
          {t('common.retry')}
        </Button>
      </Alert>
    );

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => setDraft({ ...draft, [key]: value });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    setMessage(null);
    save.mutate(draft);
  };

  return (
    <form onSubmit={submit} noValidate>
      {!query.data?.available ? <Alert tone="info">{t('voiceSettings.notAvailable')}</Alert> : null}
      <p className="mb-3 text-sm">
        {settings.callNumber
          ? t('voiceSettings.callNumber', { number: settings.callNumber })
          : settings.pendingCallNumber
            ? t('voiceSettings.callNumberPending', { number: settings.pendingCallNumber })
            : t('voiceSettings.noCallNumber')}
      </p>
      {settings.callLineProblem ? (
        <div className="mb-3">
          <Alert tone="warning">
            {/* A refused change leaves the old line answering, when there was
            one; with no line at all, the reason says so on its own. */}
            {settings.callLineProblem.reason === 'no_line'
              ? null
              : settings.callNumber
                ? `${t('voiceSettings.callLineProblemStill', { number: settings.callNumber })} `
                : `${t('voiceSettings.callLineProblemNoLine')} `}
            {t(`voiceSettings.callLineProblemReason.${settings.callLineProblem.reason}`)}
          </Alert>
        </div>
      ) : null}
      {settings.recordingsLeftOnTwilio > 0 ? (
        <div className="mb-3">
          <Alert tone="warning">
            {t('voiceSettings.recordingsLeftOnTwilio', { count: settings.recordingsLeftOnTwilio })}
          </Alert>
        </div>
      ) : null}
      {message ? (
        <div className="mb-3">
          <Alert tone={message.tone}>{message.text}</Alert>
        </div>
      ) : null}

      <TextInput
        label={t('voiceSettings.forwardTo')}
        hint={t('voiceSettings.forwardToHint')}
        type="tel"
        autoComplete="tel"
        value={draft.forwardTo}
        error={fieldErrors.forwardTo}
        onChange={(event) => set('forwardTo', event.target.value)}
      />
      <Select
        label={t('voiceSettings.ringTimeout')}
        hint={t('voiceSettings.ringTimeoutHint', { seconds: RING_TIMEOUT_DEFAULT_SECONDS })}
        value={String(draft.ringTimeoutSeconds)}
        error={fieldErrors.ringTimeoutSeconds}
        onChange={(event) => set('ringTimeoutSeconds', Number(event.target.value))}
        options={RING_OPTIONS.map((seconds) => ({
          value: String(seconds),
          label: t('voiceSettings.seconds', { seconds }),
        }))}
      />

      <fieldset className="mt-4">
        <legend className="text-sm font-semibold">{t('voiceSettings.greeting')}</legend>
        <label className="mt-2 flex items-center gap-2 text-sm">
          <input
            type="radio"
            name="greeting-kind"
            checked={draft.greetingKind === 'tts'}
            onChange={() => set('greetingKind', 'tts')}
          />
          {t('voiceSettings.greetingTts')}
        </label>
        <label className="mt-1 flex items-center gap-2 text-sm">
          <input
            type="radio"
            name="greeting-kind"
            checked={draft.greetingKind === 'audio'}
            disabled={!settings.hasGreetingAudio}
            onChange={() => set('greetingKind', 'audio')}
          />
          {t('voiceSettings.greetingAudio')}
        </label>
        {fieldErrors.greetingKind ? (
          <p className="field-error" role="alert">
            {fieldErrors.greetingKind}
          </p>
        ) : null}
        {draft.greetingKind === 'tts' ? (
          <TextArea
            label={t('voiceSettings.greetingText')}
            hint={t('voiceSettings.greetingTextHint', { max: VOICE_GREETING_MAX_CHARS })}
            maxLength={VOICE_GREETING_MAX_CHARS}
            rows={3}
            value={draft.greetingText}
            error={fieldErrors.greetingText}
            onChange={(event) => set('greetingText', event.target.value)}
          />
        ) : null}
        <div className="mt-2">
          {settings.hasGreetingAudio ? (
            <>
              {/* eslint-disable-next-line jsx-a11y/media-has-caption -- the owner's own greeting */}
              <audio
                className="w-full"
                controls
                preload="none"
                src={companyApi.voiceGreetingUrl()}
                aria-label={t('voiceSettings.playGreeting')}
              />
              <Button
                variant="secondary"
                busy={removeGreeting.isPending}
                onClick={() => removeGreeting.mutate()}
                type="button"
              >
                {t('voiceSettings.removeGreeting')}
              </Button>
            </>
          ) : null}
          <input
            ref={greetingInput}
            className="sr-only"
            tabIndex={-1}
            type="file"
            accept="audio/mpeg,audio/wav,.mp3,.wav"
            aria-label={t('voiceSettings.uploadGreeting')}
            disabled={upload.isPending}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) upload.mutate(file);
              event.target.value = '';
            }}
          />
          <div className="mt-2">
            <Button
              variant="secondary"
              busy={upload.isPending}
              onClick={() => greetingInput.current?.click()}
              type="button"
            >
              {t('voiceSettings.uploadGreeting')}
            </Button>
          </div>
          <p className="field-hint">{t('voiceSettings.uploadGreetingHint')}</p>
        </div>
      </fieldset>

      <div className="mt-4">
        <Checkbox
          label={t('voiceSettings.recordAnswered')}
          hint={t('voiceSettings.recordAnsweredHint')}
          checked={draft.recordAnswered}
          onChange={(event) => set('recordAnswered', event.target.checked)}
        />
        <Checkbox
          label={t('voiceSettings.transcribe')}
          hint={t('voiceSettings.transcribeHint')}
          checked={draft.transcribe}
          onChange={(event) => set('transcribe', event.target.checked)}
        />
      </div>

      {/* Company defaults a location or a number can override (the Numbers card below). */}
      <div className="mt-4">
        <Select
          label={t('voiceSettings.voicemailNotify')}
          hint={t('voiceSettings.voicemailNotifyHint')}
          value={draft.voicemailNotify}
          error={fieldErrors.voicemailNotify}
          onChange={(event) => set('voicemailNotify', event.target.value as VoicemailNotify)}
          options={VOICEMAIL_NOTIFY_OPTIONS.map((value) => ({
            value,
            label: t(`voiceRouting.voicemailNotify.${value}`),
          }))}
        />
        <Select
          label={t('voiceSettings.afterHours')}
          hint={t('voiceSettings.afterHoursHint')}
          value={draft.afterHours}
          error={fieldErrors.afterHours}
          onChange={(event) => set('afterHours', event.target.value as AfterHoursPolicy)}
          options={AFTER_HOURS_OPTIONS.map((value) => ({ value, label: t(`voiceRouting.afterHours.${value}`) }))}
        />
      </div>

      {settings.webhooks ? (
        <div className="mt-4 text-sm">
          <p className="font-semibold">{t('voiceSettings.ownAccount')}</p>
          <p className="field-hint">{t('voiceSettings.ownAccountHint')}</p>
          <p className="mt-1 break-all">
            {t('voiceSettings.incomingUrl')}: <code>{settings.webhooks.incomingUrl}</code>
          </p>
          <p className="mt-1 break-all">
            {t('voiceSettings.statusUrl')}: <code>{settings.webhooks.statusUrl}</code>
          </p>
        </div>
      ) : null}

      <div className="mt-4">
        <Button type="submit" busy={save.isPending} disabled={!query.data?.available}>
          {t('voiceSettings.save')}
        </Button>
      </div>
    </form>
  );
}
